import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Bell, 
  WifiOff, 
  Zap, 
  Gauge, 
  AlertTriangle,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPage = () => {
  const { t } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`);
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'device_offline':
        return <WifiOff className="w-5 h-5" />;
      case 'power_loss':
        return <Zap className="w-5 h-5" />;
      case 'pressure_abnormal':
        return <Gauge className="w-5 h-5" />;
      case 'valve_not_responding':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'device_offline':
        return 'text-fac-red bg-fac-red/10';
      case 'power_loss':
        return 'text-fac-yellow bg-fac-yellow/10';
      case 'pressure_abnormal':
        return 'text-fac-blue bg-fac-blue/10';
      default:
        return 'text-fac-yellow bg-fac-yellow/10';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading font-bold text-3xl text-white uppercase" data-testid="alerts-title">
            {t('alerts_title')}
            <span className="block text-fac-green font-chinese text-xl mt-1">警報</span>
          </h1>
          
          {unreadCount > 0 && (
            <span className="px-3 py-1 bg-fac-red text-white text-sm font-bold rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>

        {/* Alerts List */}
        <Card className="bg-fac-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Bell className="w-5 h-5 text-fac-yellow" />
              Notifications / 通知
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-fac-paper rounded-lg">
                    <div className="w-10 h-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground font-chinese">
                  {t('alerts_empty')}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="alerts-list">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`flex items-start gap-4 p-4 rounded-lg border ${
                      alert.is_read 
                        ? 'bg-fac-paper border-border opacity-60' 
                        : 'bg-fac-paper border-fac-yellow/50'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getAlertColor(alert.alert_type)}`}>
                      {getAlertIcon(alert.alert_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium font-chinese">
                        {alert.message}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {alert.device_name} • {formatDate(alert.created_at)}
                      </p>
                    </div>
                    
                    {!alert.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(alert.id)}
                        className="text-fac-green hover:bg-fac-green/10"
                        data-testid={`mark-read-${alert.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert Types Info */}
        <Card className="bg-fac-card border-border mt-6">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase text-lg">
              Alert Types / 警報類型
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-fac-red/10 flex items-center justify-center">
                  <WifiOff className="w-4 h-4 text-fac-red" />
                </div>
                <span className="text-muted-foreground font-chinese text-sm">
                  {t('alert_device_offline')} / 設備已離線
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-fac-yellow/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-fac-yellow" />
                </div>
                <span className="text-muted-foreground font-chinese text-sm">
                  {t('alert_power_loss')} / 電源中斷
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-fac-blue/10 flex items-center justify-center">
                  <Gauge className="w-4 h-4 text-fac-blue" />
                </div>
                <span className="text-muted-foreground font-chinese text-sm">
                  Pressure Issue / 壓力異常
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-fac-yellow/10 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-fac-yellow" />
                </div>
                <span className="text-muted-foreground font-chinese text-sm">
                  {t('alert_valve_error')} / 閥門無回應
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AlertsPage;
